/* @ngInject */

export default (
  $q,
  $rootScope,
  helperService,
  helperPdfService,
  homeLoanService,
  quickToolService,
  dateTimeService,
  OwnershipType,
  TransactionType,
  INSTANCE
) => ({
  createHomeLoanFundingWorksheet: ({
    fundingWorksheets,
    homeLoanSecurities,
    ticket,
    isQuickTool,
    isView
  }) => {
    const deferred = $q.defer();
    const organizationCountry = helperService.getOrganizationCountry();
    const currentOrganization = $rootScope.CurrentOrganization;
    const currentAccount = $rootScope.CurrentAccount;
    const currentCurrency = $rootScope.CurrentCurrency();
    const currentTimeZone = $rootScope.CurrentTimeZone();
    let totalDifference = 0;
    const fundsRequiredTable = [];
    let totalPurposeFunds = 0;
    let totalExistingLoanBalance = 0;
    let totalExitFees = 0;
    let totalStampDuty = 0;
    let totalLandTransferFees = 0;
    let totalMortgageRegistrationFees = 0;
    let totalLenderFees = 0;
    let totalLegalFees = 0;
    let totalMortgageDischargeCosts = 0;
    let totalOtherFees = 0;
    const otherDebts = [{ text: 'Other debts\n', bold: true }];
    const totalFundsRequired = helperService.isNumber(
      fundingWorksheets.fundsTotal.required,
      false
    );
    const isSomeTransactionPurchase = _.some(
      fundingWorksheets?.fundsRequired || [],
      (f) => f?.transactionType?.name === TransactionType.purchase.name
    );

    const prepareFundingWorksheetDetails = ({ fundRequired, fundAvailable }) => {
      const security = _.find(homeLoanSecurities, (s) => s.id === fundRequired.idSecurity);
      const table = [
        [
          {
            text: 'Transaction',
            bold: true,
            style: [
              'tableFontSize',
              'tableHeader',
              'tableCellLeftAlign',
              'tableCellMarginLeftTopBottom'
            ]
          },
          {
            text:
              fundRequired.transactionType?.name !== TransactionType.none.name
                ? fundRequired?.transactionType?.title || '-'
                : '-',
            style: [
              'tableFontSize',
              'tableHeader',
              'tableCellRightAlign',
              'tableCellMarginRightTopBottom'
            ]
          },
          {
            text: 'Ownership',
            bold: true,
            style: [
              'tableFontSize',
              'tableHeader',
              'tableCellLeftAlign',
              'tableCellMarginLeftTopBottom'
            ]
          },
          {
            text:
              fundRequired?.ownershipType?.name !== OwnershipType.none.name
                ? fundRequired?.ownershipType?.title || '-'
                : '-',
            style: [
              'tableFontSize',
              'tableHeader',
              'tableCellRightAlign',
              'tableCellMarginRightTopBottom'
            ]
          },
          {
            text: 'Address',
            bold: true,
            style: [
              'tableFontSize',
              'tableHeader',
              'tableCellLeftAlign',
              'tableCellMarginLeftTopBottom'
            ]
          },
          {
            text: `${
              security && security.address
                ? `${security.address.state} ${security.address.postCode}`
                : ``
            }`,
            style: [
              'tableFontSize',
              'tableHeader',
              'tableCellRightAlign',
              'tableCellMarginRightTopBottom'
            ]
          }
        ]
      ];

      if (organizationCountry?.cca2 !== 'NZ') {
        table.push(
          [
            {
              text: 'Security value',
              style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
            },
            {
              text: fundRequired.purposeFunds.formatWithCurrency(currentCurrency),
              style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
            },
            {
              text: 'Stamp duty',
              style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
            },
            {
              text: fundRequired.stampDuty.formatWithCurrency(currentCurrency),
              style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
            },
            {
              text: 'Proposed loan amount',
              style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
            },
            {
              text: fundAvailable.proposedLoanAmount.formatWithCurrency(currentCurrency),
              style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
            }
          ],
          [
            {
              text: 'Existing loan balance',
              style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
            },
            {
              text: fundRequired.existingLoanBalance.formatWithCurrency(currentCurrency),
              style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
            },
            {
              text: 'Land transfer fees',
              style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
            },
            {
              text: fundRequired.landTransferFees.formatWithCurrency(currentCurrency),
              style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
            },
            {
              text: 'Savings',
              style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
            },
            {
              text: fundAvailable.savings.formatWithCurrency(currentCurrency),
              style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
            }
          ],
          [
            {
              text: 'Exit fee',
              style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
            },
            {
              text: fundRequired.exitFee.formatWithCurrency(currentCurrency),
              style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
            },
            {
              text: 'Mortgage registration fees',
              style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
            },
            {
              text: fundRequired.mortgageRegistrationFees.formatWithCurrency(currentCurrency),
              style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
            },
            {
              text: 'Deposit paid',
              style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
            },
            {
              text: fundAvailable.depositPaid.formatWithCurrency(currentCurrency),
              style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
            }
          ],
          [
            {
              text: "Purchase/Builder's contract price",
              style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
            },
            {
              text: fundRequired.builderContractPrice.formatWithCurrency(currentCurrency),
              style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
            },
            {
              text: 'Lender fees',
              style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
            },
            {
              text: fundRequired.lenderFees.formatWithCurrency(currentCurrency),
              style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
            },
            {
              text: 'LVR',
              style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
            },
            {
              text: `${Math.round(fundAvailable.baseLvr * 100) / 100}%`,
              style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
            }
          ],
          [
            {
              text: 'Land value',
              style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
            },
            {
              text: fundRequired.landValue.formatWithCurrency(currentCurrency),
              style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
            },
            {
              text: 'Legal fees',
              style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
            },
            {
              text: fundRequired.legalFees.formatWithCurrency(currentCurrency),
              style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
            },
            '',
            ''
          ],
          [
            '',
            '',
            {
              text: 'Mortgage discharge costs',
              style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
            },
            {
              text: fundRequired.mortgageDischargeCosts.formatWithCurrency(currentCurrency),
              style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
            },
            '',
            ''
          ],
          [
            '',
            '',
            {
              text: 'Other fees/costs',
              style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
            },
            {
              text: fundRequired.otherFees.formatWithCurrency(currentCurrency),
              style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
            },
            '',
            ''
          ]
        );
      } else {
        table.push(
          [
            {
              text: 'Security value',
              style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
            },
            {
              text: fundRequired.purposeFunds.formatWithCurrency(currentCurrency),
              style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
            },
            {
              text: 'Land transfer fees',
              style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
            },
            {
              text: fundRequired.landTransferFees.formatWithCurrency(currentCurrency),
              style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
            },
            {
              text: 'Proposed loan amount',
              style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
            },
            {
              text: fundAvailable.proposedLoanAmount.formatWithCurrency(currentCurrency),
              style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
            }
          ],
          [
            {
              text: 'Existing loan balance',
              style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
            },
            {
              text: fundRequired.existingLoanBalance.formatWithCurrency(currentCurrency),
              style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
            },
            {
              text: 'Mortgage registration fees',
              style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
            },
            {
              text: fundRequired.mortgageRegistrationFees.formatWithCurrency(currentCurrency),
              style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
            },
            {
              text: 'Savings',
              style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
            },
            {
              text: fundAvailable.savings.formatWithCurrency(currentCurrency),
              style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
            }
          ],
          [
            {
              text: 'Exit fee',
              style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
            },
            {
              text: fundRequired.exitFee.formatWithCurrency(currentCurrency),
              style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
            },
            {
              text: 'Lender fees',
              style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
            },
            {
              text: fundRequired.lenderFees.formatWithCurrency(currentCurrency),
              style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
            },
            {
              text: 'Deposit paid',
              style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
            },
            {
              text: fundAvailable.depositPaid.formatWithCurrency(currentCurrency),
              style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
            }
          ],
          [
            {
              text: "Purchase/Builder's contract price",
              style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
            },
            {
              text: fundRequired.builderContractPrice.formatWithCurrency(currentCurrency),
              style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
            },
            {
              text: 'Legal fees',
              style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
            },
            {
              text: fundRequired.legalFees.formatWithCurrency(currentCurrency),
              style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
            },
            {
              text: 'LVR',
              style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
            },
            {
              text: `${Math.round(fundAvailable.baseLvr * 100) / 100}%`,
              style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
            }
          ],
          [
            {
              text: 'Land value',
              style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
            },
            {
              text: fundRequired.landValue.formatWithCurrency(currentCurrency),
              style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
            },
            {
              text: 'Mortgage discharge costs',
              style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
            },
            {
              text: fundRequired.mortgageDischargeCosts.formatWithCurrency(currentCurrency),
              style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
            },
            '',
            ''
          ],
          [
            '',
            '',
            {
              text: 'Other fees/costs',
              style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
            },
            {
              text: fundRequired.otherFees.formatWithCurrency(currentCurrency),
              style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
            },
            '',
            ''
          ]
        );
      }
      return table;
    };

    for (let i = 0; i < fundingWorksheets.fundsRequired.length; i += 1) {
      const fundRequired = fundingWorksheets.fundsRequired[i];
      const fA = fundingWorksheets.fundsAvailable.length > 1 ? i : 0;
      const fundAvailable = fundingWorksheets.fundsAvailable[fA];
      totalPurposeFunds += helperService.isNumber(fundRequired.purposeFunds, true);
      totalExistingLoanBalance += helperService.isNumber(
        fundRequired.existingLoanBalance,
        true
      );
      totalExitFees += helperService.isNumber(fundRequired.exitFee, true);
      totalStampDuty += helperService.isNumber(fundRequired.stampDuty, true);
      totalLandTransferFees += helperService.isNumber(fundRequired.landTransferFees, true);
      totalMortgageRegistrationFees += helperService.isNumber(
        fundRequired.mortgageRegistrationFees,
        true
      );
      totalLenderFees += helperService.isNumber(fundRequired.lenderFees, true);
      totalLegalFees += isSomeTransactionPurchase
        ? helperService.isNumber(fundRequired.legalFees, true)
        : 0;
      totalMortgageDischargeCosts += helperService.isNumber(
        fundRequired.mortgageDischargeCosts,
        true
      );
      totalOtherFees += helperService.isNumber(fundRequired.otherFees, true);
      if (fundRequired?.otherDebts?.length) {
        for (let j = 0; j < fundRequired.otherDebts.length; j += 1) {
          const otherDebt = {
            text: `- ${fundRequired.otherDebts[j]?.name || 'N/A'}: ${(
              fundRequired.otherDebts[j]?.value || 0
            ).formatWithCurrency(currentCurrency)}${
              j !== fundRequired.otherDebts.length - 1 ? `\n` : ``
            }`,
            fontSize: 10
          };
          otherDebts.push(otherDebt);
        }
      } else {
        otherDebts.push({ text: '- No debts.', fontSize: 10 });
      }

      const fundRequiredTable = prepareFundingWorksheetDetails({
        fundRequired,
        fundAvailable
      });
      fundsRequiredTable.push(fundRequiredTable);
    }

    let totalLoanAmount = 0;
    let totalFhog = 0;
    let totalSaleProceedFunds = 0;
    let totalSavings = 0;
    let totalDepositPaid = 0;
    let totalEquityFromProperty = 0;
    let totalGift = 0;
    let totalOtherFundsAvailable = 0;
    let totalLmi = 0;
    let totalLoanAmountWithLmi = 0;
    const totalFundsAvailable = helperService.isNumber(
      fundingWorksheets.fundsTotal.available,
      true
    );
    for (let i = 0; i < fundingWorksheets.fundsAvailable.length; i += 1) {
      const fundAvailable = fundingWorksheets.fundsAvailable[i];
      totalLoanAmount += helperService.isNumber(fundAvailable.proposedLoanAmount, true);
      totalFhog += helperService.isNumber(fundAvailable.firstHomeOwnersGrant, true);
      totalSaleProceedFunds += helperService.isNumber(fundAvailable.saleProceedFunds, true);
      totalSavings += helperService.isNumber(fundAvailable.savings, true);
      totalDepositPaid += helperService.isNumber(fundAvailable.depositPaid, true);
      totalEquityFromProperty += helperService.isNumber(fundAvailable.equityFromProperty, true);
      totalGift += helperService.isNumber(fundAvailable.gift, true);
      totalOtherFundsAvailable += helperService.isNumber(
        fundAvailable.otherFundsAvailable,
        true
      );
      totalLmi += helperService.isNumber(fundAvailable.lmi, true);
      totalLoanAmountWithLmi += fundAvailable.isCapitalize
        ? helperService.isNumber(fundAvailable.proposedLoanAmount, true) +
          helperService.isNumber(fundAvailable.lmi, true)
        : helperService.isNumber(fundAvailable.proposedLoanAmount, true);
    }
    totalDifference = totalFundsAvailable - totalFundsRequired;

    const fundingWorksheetTable = [
      [
        {
          colSpan: 2,
          text: 'Funds required',
          bold: true,
          style: ['tableFontSize', 'tableHeader', 'tableCellCenterAlign', 'tableCellMarginTop']
        },
        '',
        {
          colSpan: 2,
          text: 'Funds available',
          bold: true,
          style: ['tableFontSize', 'tableHeader', 'tableCellCenterAlign', 'tableCellMarginTop']
        },
        ''
      ],
      [
        {
          text: 'Security value',
          style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
        },
        {
          text: totalPurposeFunds.formatWithCurrency(currentCurrency),
          style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
        },
        {
          text: 'Proposed loan amount',
          style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
        },
        {
          text: totalLoanAmount.formatWithCurrency(currentCurrency),
          style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
        }
      ],
      [
        {
          text: 'Existing loan balance',
          style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
        },
        {
          text: totalExistingLoanBalance.formatWithCurrency(currentCurrency),
          style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
        },
        {
          text: 'FHOG',
          style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
        },
        {
          text: totalFhog.formatWithCurrency(currentCurrency),
          style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
        }
      ],
      [
        {
          text: 'Exit fee',
          style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
        },
        {
          text: totalExitFees.formatWithCurrency(currentCurrency),
          style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
        },
        {
          text: 'Deposit paid',
          style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
        },
        {
          text: totalDepositPaid.formatWithCurrency(currentCurrency),
          style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
        }
      ]
    ];

    if (organizationCountry.cca2 !== 'NZ') {
      fundingWorksheetTable.push(
        [
          {
            text: 'Stamp duty',
            style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
          },
          {
            text: totalStampDuty.formatWithCurrency(currentCurrency),
            style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
          },
          {
            text: 'Savings',
            style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
          },
          {
            text: totalSavings.formatWithCurrency(currentCurrency),
            style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
          }
        ],
        [
          {
            text: 'Land transfer fees',
            style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
          },
          {
            text: totalLandTransferFees.formatWithCurrency(currentCurrency),
            style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
          },
          {
            text: 'Sale proceed funds',
            style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
          },
          {
            text: totalSaleProceedFunds.formatWithCurrency(currentCurrency),
            style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
          }
        ],
        [
          {
            text: 'Mortgage registration fees',
            style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
          },
          {
            text: totalMortgageRegistrationFees.formatWithCurrency(currentCurrency),
            style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
          },
          {
            text: 'Equity from property',
            style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
          },
          {
            text: totalEquityFromProperty.formatWithCurrency(currentCurrency),
            style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
          }
        ],
        [
          {
            text: 'Legal fees',
            style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
          },
          {
            text: totalLegalFees.formatWithCurrency(currentCurrency),
            style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
          },
          {
            text: 'Gift',
            style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
          },
          {
            text: totalGift.formatWithCurrency(currentCurrency),
            style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
          }
        ],
        [
          {
            text: 'Lender fees',
            style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
          },
          {
            text: totalLenderFees.formatWithCurrency(currentCurrency),
            style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
          },
          {
            text: 'Total other funds available',
            style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
          },
          {
            text: totalOtherFundsAvailable.formatWithCurrency(currentCurrency),
            style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
          }
        ],
        [
          {
            text: 'Mortgage discharge costs',
            style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
          },
          {
            text: totalMortgageDischargeCosts.formatWithCurrency(currentCurrency),
            style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
          },
          {
            text: fundingWorksheets.fundsAvailable[0].isCapitalize ? 'LMI (Capitalized)' : '',
            style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
          },
          {
            text: fundingWorksheets.fundsAvailable[0].isCapitalize
              ? totalLmi.formatWithCurrency(currentCurrency)
              : '',
            style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
          }
        ],
        [
          {
            text: 'LMI',
            style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
          },
          {
            text: totalLmi.formatWithCurrency(currentCurrency),
            style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
          },
          {
            border: [true, true, true, false],
            text: 'Total loan amount',
            style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
          },
          {
            border: [true, true, true, false],
            text: totalLoanAmountWithLmi.formatWithCurrency(currentCurrency),
            style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
          }
        ],
        [
          {
            text: 'Other fees/Costs',
            style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
          },
          {
            text: totalOtherFees.formatWithCurrency(currentCurrency),
            style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
          },
          {
            border: [true, false, true, false],
            text: 'Total security value',
            style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
          },
          {
            border: [true, false, true, false],
            text: totalPurposeFunds.formatWithCurrency(currentCurrency),
            style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
          }
        ],
        [
          {
            colSpan: 2,
            rowSpan: 1,
            text: otherDebts,
            style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
          },
          '',
          {
            border: [true, false, true, true],
            text: 'Total LVR',
            style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
          },
          {
            border: [true, false, true, true],
            text:
              fundingWorksheets.fundsAvailable.length === 1 ||
              fundingWorksheets.fundsAvailable[0].isCrossCollateralized
                ? `${Math.round(fundingWorksheets.fundsAvailable[0].totalLvr * 100) / 100}%`
                : 'Please refer to tables below',
            style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
          }
        ],
        [
          {
            text: 'Total fund required (A)',
            bold: true,
            style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
          },
          {
            text: totalFundsRequired.formatWithCurrency(currentCurrency),
            bold: true,
            style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
          },
          {
            text: 'Total funds available (B)',
            bold: true,
            style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
          },
          {
            text: totalFundsAvailable.formatWithCurrency(currentCurrency),
            bold: true,
            style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
          }
        ],
        [
          {
            colSpan: 4,
            text: `${
              totalDifference >= 0 ? `Funds surplus (B-A)` : `Funds deficit (B-A)`
            }: ${totalDifference.formatWithCurrency(currentCurrency)}`,
            bold: true,
            color: `${totalDifference >= 0 ? `green` : `red`}`,
            style: ['tableFontSize', 'tableCellCenterAlign', 'tableCellMarginTopBottom']
          },
          '',
          '',
          ''
        ]
      );
    } else {
      fundingWorksheetTable.push(
        [
          {
            text: 'Land transfer fees',
            style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
          },
          {
            text: totalLandTransferFees.formatWithCurrency(currentCurrency),
            style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
          },
          {
            text: 'Savings',
            style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
          },
          {
            text: totalSavings.formatWithCurrency(currentCurrency),
            style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
          }
        ],
        [
          {
            text: 'Mortgage registration fees',
            style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
          },
          {
            text: totalMortgageRegistrationFees.formatWithCurrency(currentCurrency),
            style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
          },
          {
            text: 'Sale proceed funds',
            style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
          },
          {
            text: totalSaleProceedFunds.formatWithCurrency(currentCurrency),
            style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
          }
        ],
        [
          {
            text: 'Legal fees',
            style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
          },
          {
            text: totalLegalFees.formatWithCurrency(currentCurrency),
            style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
          },
          {
            text: 'Equity from property',
            style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
          },
          {
            text: totalEquityFromProperty.formatWithCurrency(currentCurrency),
            style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
          }
        ],
        [
          {
            text: 'Lender fees',
            style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
          },
          {
            text: totalLenderFees.formatWithCurrency(currentCurrency),
            style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
          },
          {
            text: 'Gift',
            style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
          },
          {
            text: totalGift.formatWithCurrency(currentCurrency),
            style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
          }
        ],
        [
          {
            text: 'Mortgage discharge costs',
            style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
          },
          {
            text: totalMortgageDischargeCosts.formatWithCurrency(currentCurrency),
            style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
          },
          {
            text: 'Total other funds available',
            style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
          },
          {
            text: totalOtherFundsAvailable.formatWithCurrency(currentCurrency),
            style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
          }
        ],
        [
          {
            text: 'LMI',
            style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
          },
          {
            text: totalLmi.formatWithCurrency(currentCurrency),
            style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
          },
          {
            text: fundingWorksheets.fundsAvailable[0].isCapitalize ? 'LMI (Capitalized)' : '',
            style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
          },
          {
            text: fundingWorksheets.fundsAvailable[0].isCapitalize
              ? totalLmi.formatWithCurrency(currentCurrency)
              : '',
            style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
          }
        ],
        [
          {
            text: 'Other fees/Costs',
            style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
          },
          {
            text: totalOtherFees.formatWithCurrency(currentCurrency),
            style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
          },
          {
            border: [true, true, true, false],
            text: 'Total loan amount',
            style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
          },
          {
            border: [true, true, true, false],
            text: totalLoanAmountWithLmi.formatWithCurrency(currentCurrency),
            style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
          }
        ],
        [
          {
            colSpan: 2,
            rowSpan: 2,
            text: otherDebts,
            style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
          },
          '',
          {
            border: [true, false, true, false],
            text: 'Total security value',
            style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
          },
          {
            border: [true, false, true, false],
            text: totalPurposeFunds.formatWithCurrency(currentCurrency),
            style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
          }
        ],
        [
          '',
          '',
          {
            border: [true, false, true, true],
            text: 'Total LVR',
            style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
          },
          {
            border: [true, false, true, true],
            text:
              fundingWorksheets.fundsAvailable.length === 1 ||
              fundingWorksheets.fundsAvailable[0].isCrossCollateralized
                ? `${Math.round(fundingWorksheets.fundsAvailable[0].totalLvr * 100) / 100}%`
                : 'Please refer to tables below',
            style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
          }
        ],
        [
          {
            text: 'Total fund required (A)',
            bold: true,
            style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
          },
          {
            text: totalFundsRequired.formatWithCurrency(currentCurrency),
            bold: true,
            style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
          },
          {
            text: 'Total funds available (B)',
            bold: true,
            style: ['tableFontSize', 'tableCellLeftAlign', 'tableCellMarginLeftTopBottom']
          },
          {
            text: totalFundsAvailable.formatWithCurrency(currentCurrency),
            bold: true,
            style: ['tableFontSize', 'tableCellRightAlign', 'tableCellMarginRightTopBottom']
          }
        ],
        [
          {
            colSpan: 4,
            text: `${
              totalDifference >= 0 ? `Funds surplus (B-A)` : `Funds deficit (B-A)`
            }: ${totalDifference.formatWithCurrency(currentCurrency)}`,
            bold: true,
            color: `${totalDifference >= 0 ? `green` : `red`}`,
            style: ['tableFontSize', 'tableCellCenterAlign', 'tableCellMarginTopBottom']
          },
          '',
          '',
          ''
        ]
      );
    }
    const now = moment.utc().tz(currentTimeZone).format('X');
    const date = dateTimeService.getFormattedDate(now);
    const orgLogo = currentOrganization?.organization?.logo
      ? currentOrganization.organization.logo.replace(
          /([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/gi,
          currentOrganization.id
        )
      : INSTANCE.url + INSTANCE.logo;
    quickToolService.getBase64FromImageUrl({ url: orgLogo }).then((res) => {
      const docDefinition = {
        pageSize: 'A4',
        pageOrientation: 'landscape',
        pageMargins: [40, 40, 40, 40],
        content: [
          {
            table: {
              widths: [240, '*'],
              headerRows: 1,
              body: [
                [
                  {
                    width: 180,
                    height: 30,
                    image: res.data,
                    margin: [5, 3, 0, 3],
                    style: 'tableHeader'
                  },
                  {
                    fillColor: '#eee',
                    text: `Prepared by: ${currentAccount.getName()}\nPrepared on: ${date}`,
                    style: 'prepared'
                  }
                ]
              ]
            },
            layout: 'noBorders'
          },
          {
            text: 'Funding worksheet',
            style: ['header', 'marginTop']
          },
          {
            text: `Based on the information provided the following funding worksheet structure is completed.\nThe funding worksheet below confirms that there are ${
              fundingWorksheets.fundsTotal.difference < 0 ? `insufficient` : `sufficient`
            } funds available to complete the proposed transaction.`,
            fontSize: 11,
            margin: [0, 7, 0, 0]
          },
          {
            table: {
              widths: ['*', '*', '*', '*'],
              headerRows: 1,
              body: fundingWorksheetTable
            },
            style: ['marginTop'],
            layout: 'defaultLayout'
          },
          {
            text:
              fundingWorksheets.fundsTotal.difference < 0
                ? 'Additional funds contribution is required or increase in the total loan amount.'
                : '',
            pageBreak: 'after',
            fontSize: 11,
            margin: [0, 10, 0, 0]
          }
        ],
        styles: {
          header: {
            fontSize: 16,
            bold: true
          },
          subHeader: {
            fontSize: 12,
            bold: true,
            margin: [0, 10, 0, 4]
          },
          prepared: {
            fontSize: 10,
            alignment: 'right',
            margin: [0, 6, 5, 0]
          },
          marginTop: {
            margin: [0, 10, 0, 0]
          },
          tableFontSize: {
            fontSize: 10
          },
          tableHeader: {
            fillColor: '#eee'
          },
          tableCellMarginTop: {
            margin: [0, 2, 0, 0]
          },
          tableCellMarginLeftTopBottom: {
            margin: [4, 2, 0, 2]
          },
          tableCellMarginRightTopBottom: {
            margin: [0, 2, 4, 2]
          },
          tableCellMarginTopBottom: {
            margin: [0, 2, 0, 2]
          },
          tableCellLeftAlign: {
            alignment: 'left'
          },
          tableCellRightAlign: {
            alignment: 'right'
          },
          tableCellCenterAlign: {
            alignment: 'center'
          }
        }
      };

      if (fundsRequiredTable.length) {
        fundsRequiredTable.forEach((fundRequiredTable, idx) => {
          docDefinition.content.push(
            {
              text: `Security #${idx + 1}`,
              style: 'subHeader'
            },
            {
              table: {
                dontBreakRows: true,
                widths: ['*', '*', '*', '*', '*', '*'],
                headerRows: 1,
                body: fundRequiredTable
              },
              layout: 'defaultLayout'
            }
          );
        });
      }
      pdfMake.tableLayouts = {
        defaultLayout: {
          hLineWidth(i, node) {
            return i === node.table.headerRows ? 1 : 0.5;
          },
          vLineWidth(i) {
            return i === 1 || i === 3 || i === 5 ? 0 : 0.5;
          },
          hLineColor(i, node) {
            return i === 0 || i === node.table.body.length ? '#333' : '#aaa';
          },
          vLineColor(i, node) {
            return i === 0 || i === node.table.widths.length ? '#333' : '#aaa';
          },
          paddingLeft() {
            return 0;
          },
          paddingRight() {
            return 0;
          },
          paddingTop() {
            return 0;
          },
          paddingBottom() {
            return 0;
          }
        }
      };
      const fileName = `${moment.utc().year()}${moment.utc().month() + 1}${moment
        .utc()
        .date()}${isQuickTool ? ' Quick tools -' : ''} Funding worksheet.pdf`;
      if (isQuickTool && !_.isEmpty(docDefinition)) {
        if (!_.isEmpty(docDefinition)) {
          if (isView) {
            pdfMake.createPdf(docDefinition).open();
          } else {
            pdfMake.createPdf(docDefinition).download(fileName);
          }
          deferred.resolve();
        }
      } else if (!isQuickTool && !_.isEmpty(docDefinition)) {
        if (isView) {
          helperPdfService.viewOrSavePdf({ docDefinition, isView: true }).then(() => {
            deferred.resolve();
          });
        } else {
          helperPdfService
            .viewOrSavePdf({
              ticket,
              docDefinition,
              fileName,
              isView: false
            })
            .then(() => {
              deferred.resolve();
            });
        }
      }
    });
    return deferred.promise;
  },
  prepareHomeLoanFundingWorksheetForApplicationForm: ({
    fundingWorksheets,
    homeLoanSecurities,
    pageBreak
  }) => {
    const content = [];
    const currentCurrency = $rootScope.CurrentCurrency();
    const isSomeTransactionPurchase = _.some(
      fundingWorksheets?.fundsRequired || [],
      (f) => f?.transactionType?.name === TransactionType.purchase.name
    );
    if (
      fundingWorksheets &&
      (fundingWorksheets?.fundsRequired?.length || fundingWorksheets?.fundsAvailable?.length)
    ) {
      if (!pageBreak) {
        content.push({
          text: '',
          margin: [0, 20, 0, 0]
        });
      }
      content.push(
        helperPdfService.createHeader({
          title: 'FUNDING WORKSHEET',
          pageBreak: pageBreak || ''
        })
      );
      const totalsOfFundsRequired = helperPdfService.getTotalsOfFundsRequired({
        homeLoanSecurities,
        fundingWorksheets
      });
      const totalPurposeFunds = totalsOfFundsRequired?.totalPurposeFunds || 0;
      const totalExistingLoanBalance = totalsOfFundsRequired?.totalExistingLoanBalance || 0;
      const totalExitFees = totalsOfFundsRequired?.totalExitFees || 0;
      const totalStampDuty = totalsOfFundsRequired?.totalStampDuty || 0;
      const totalLandTransferFees = totalsOfFundsRequired?.totalLandTransferFees || 0;
      const totalMortgageRegistrationFees =
        totalsOfFundsRequired?.totalMortgageRegistrationFees || 0;
      const totalLenderFees = totalsOfFundsRequired?.totalLenderFees || 0;
      const totalLegalFees = isSomeTransactionPurchase
        ? totalsOfFundsRequired?.totalLegalFees || 0
        : 0;
      const totalMortgageDischargeCosts =
        totalsOfFundsRequired?.totalMortgageDischargeCosts || 0;
      const totalOtherFees = totalsOfFundsRequired?.totalOtherFees || 0;
      const totalOtherDebts = totalsOfFundsRequired?.totalOtherDebts || 0;
      const fundsRequiredData = totalsOfFundsRequired?.fundsRequiredData || [];
      const totalFundsRequired = helperService.isNumber(
        fundingWorksheets.fundsTotal.required,
        false
      );

      const totalsOfFundsAvailable = helperPdfService.getTotalsOfFundsAvailable({
        fundsAvailable: fundingWorksheets.fundsAvailable
      });
      const totalLoanAmount = totalsOfFundsAvailable?.totalLoanAmount || 0;
      const totalFhog = totalsOfFundsAvailable?.totalFhog || 0;
      const totalSaleProceedFunds = totalsOfFundsAvailable?.totalSaleProceedFunds || 0;
      const totalSavings = totalsOfFundsAvailable?.totalSavings || 0;
      const totalDepositPaid = totalsOfFundsAvailable?.totalDepositPaid || 0;
      const totalEquityFromProperty = totalsOfFundsAvailable?.totalEquityFromProperty || 0;
      const totalGift = totalsOfFundsAvailable?.totalGift || 0;
      const totalOtherFundsAvailable = totalsOfFundsAvailable?.totalOtherFundsAvailable || 0;
      const totalLmi = totalsOfFundsAvailable?.totalLmi || 0;
      const totalLoanAmountWithLmi = totalsOfFundsAvailable?.totalLoanAmountWithLmi || 0;
      const totalFundsAvailable = helperService.isNumber(
        fundingWorksheets.fundsTotal.available,
        true
      );
      const totalDifference = _.subtract(totalFundsAvailable, totalFundsRequired);
      const isLmiCapitalized = fundingWorksheets.fundsAvailable[0]?.isCapitalize || false;
      const isCrossCollateralized =
        fundingWorksheets.fundsAvailable?.length === 1 ||
        fundingWorksheets.fundsAvailable[0]?.isCrossCollateralized;

      content.push({
        table: {
          widths: ['48%', '4%', '48%'],
          body: [
            [
              helperPdfService.createSubHeader({
                title: 'FUNDS REQUIRED'
              }),
              '',
              helperPdfService.createSubHeader({
                title: 'FUNDS AVAILABLE'
              })
            ],
            [
              {
                text: [
                  {
                    text: 'Security value\n',
                    style: 'label'
                  },
                  {
                    text: totalPurposeFunds?.formatWithCurrency(currentCurrency, 0),
                    style: 'value'
                  }
                ]
              },
              '',
              {
                text: [
                  {
                    text: 'Proposed loan amount\n',
                    style: 'label'
                  },
                  {
                    text: totalLoanAmount?.formatWithCurrency(currentCurrency, 0),
                    style: 'value'
                  }
                ]
              }
            ],
            [
              {
                text: [
                  { text: 'Existing loan balance\n', style: 'label' },
                  {
                    text: totalExistingLoanBalance?.formatWithCurrency(currentCurrency, 0),
                    style: 'value'
                  }
                ]
              },
              '',
              {
                text: [
                  { text: 'FHOG\n', style: 'label' },
                  {
                    text: totalFhog?.formatWithCurrency(currentCurrency, 0),
                    style: 'value'
                  }
                ]
              }
            ],
            [
              {
                text: [
                  { text: 'Exit fee\n', style: 'label' },
                  {
                    text: totalExitFees?.formatWithCurrency(currentCurrency, 0),
                    style: 'value'
                  }
                ]
              },
              '',
              {
                text: [
                  { text: 'Deposit paid\n', style: 'label' },
                  {
                    text: totalDepositPaid?.formatWithCurrency(currentCurrency, 0),
                    style: 'value'
                  }
                ]
              }
            ],
            [
              {
                text: [
                  { text: 'Stamp duty\n', style: 'label' },
                  {
                    text: totalStampDuty?.formatWithCurrency(currentCurrency, 0),
                    style: 'value'
                  }
                ]
              },
              '',
              {
                text: [
                  { text: 'Savings\n', style: 'label' },
                  {
                    text: totalSavings?.formatWithCurrency(currentCurrency, 0),
                    style: 'value'
                  }
                ]
              }
            ],
            [
              {
                text: [
                  {
                    text: 'Land transfer fees\n',
                    style: 'label'
                  },
                  {
                    text: totalLandTransferFees?.formatWithCurrency(currentCurrency, 0),
                    style: 'value'
                  }
                ]
              },
              '',
              {
                text: [
                  {
                    text: 'Sale proceed funds\n',
                    style: 'label'
                  },
                  {
                    text: totalSaleProceedFunds?.formatWithCurrency(currentCurrency, 0),
                    style: 'value'
                  }
                ]
              }
            ],
            [
              {
                text: [
                  { text: 'Mortgage registration fees\n', style: 'label' },
                  {
                    text: totalMortgageRegistrationFees?.formatWithCurrency(currentCurrency, 0),
                    style: 'value'
                  }
                ]
              },
              '',
              {
                text: [
                  { text: 'Equity from property\n', style: 'label' },
                  {
                    text: totalEquityFromProperty?.formatWithCurrency(currentCurrency, 0),
                    style: 'value'
                  }
                ]
              }
            ],
            [
              {
                text: [
                  { text: 'Legal fees\n', style: 'label' },
                  {
                    text: totalLenderFees?.formatWithCurrency(currentCurrency, 0),
                    style: 'value'
                  }
                ]
              },
              '',
              {
                text: [
                  { text: 'Gift\n', style: 'label' },
                  {
                    text: totalGift?.formatWithCurrency(currentCurrency, 0),
                    style: 'value'
                  }
                ]
              }
            ],
            [
              {
                text: [
                  { text: 'Lender fees\n', style: 'label' },
                  {
                    text: totalLegalFees?.formatWithCurrency(currentCurrency, 0),
                    style: 'value'
                  }
                ]
              },
              '',
              {
                text: [
                  { text: 'Total other funds available\n', style: 'label' },
                  {
                    text: totalOtherFundsAvailable?.formatWithCurrency(currentCurrency, 0),
                    style: 'value'
                  }
                ]
              }
            ],
            [
              {
                text: [
                  {
                    text: 'Mortgage discharge costs\n',
                    style: 'label'
                  },
                  {
                    text: totalMortgageDischargeCosts?.formatWithCurrency(currentCurrency, 0),
                    style: 'value'
                  }
                ]
              },
              '',
              {
                text: [
                  {
                    text: `LMI ${isLmiCapitalized ? '(Capitalized)' : ''}\n`,
                    style: 'label'
                  },
                  {
                    text: totalLmi?.formatWithCurrency(currentCurrency, 0),
                    style: 'value'
                  }
                ]
              }
            ],
            [
              {
                text: [
                  { text: 'LMI\n', style: 'label' },
                  {
                    text: totalLmi?.formatWithCurrency(currentCurrency, 0),
                    style: 'value'
                  }
                ]
              },
              '',
              {
                text: [
                  { text: 'Total loan amount\n', style: 'label' },
                  {
                    text: totalLoanAmountWithLmi?.formatWithCurrency(currentCurrency, 0),
                    style: 'value'
                  }
                ]
              }
            ],
            [
              {
                text: [
                  { text: 'Other fees\n', style: 'label' },
                  {
                    text: totalOtherFees?.formatWithCurrency(currentCurrency, 0),
                    style: 'value'
                  }
                ]
              },
              '',
              {
                text: [
                  { text: 'Total security value\n', style: 'label' },
                  {
                    text: totalPurposeFunds?.formatWithCurrency(currentCurrency, 0),
                    style: 'value'
                  }
                ]
              }
            ],
            [
              {
                text: [
                  { text: 'Other debts fees\n', style: 'label' },
                  {
                    text: totalOtherDebts?.formatWithCurrency(currentCurrency, 0),
                    style: 'value'
                  }
                ]
              },
              '',
              {
                text: [
                  { text: 'Total LVR\n', style: 'label' },
                  {
                    text: isCrossCollateralized
                      ? `${helperService.round({
                          value: fundingWorksheets.fundsAvailable[0]?.totalLvr
                            ? _.divide(
                                _.multiply(fundingWorksheets.fundsAvailable[0].totalLvr, 100),
                                100
                              )
                            : 0
                        })}%`
                      : 'Please refer to data below',
                    style: 'value'
                  }
                ]
              }
            ],
            [
              {
                text: [
                  {
                    text: 'Total funds required\n',
                    style: 'label'
                  },
                  {
                    text: totalFundsRequired?.formatWithCurrency(currentCurrency, 0),
                    style: 'value'
                  }
                ]
              },
              '',
              {
                text: [
                  {
                    text: 'Total funds available\n',
                    style: 'label'
                  },
                  {
                    text: totalFundsAvailable?.formatWithCurrency(currentCurrency, 0),
                    style: 'value'
                  }
                ]
              }
            ]
          ]
        },
        layout: 'noBorders'
      });

      content.push(
        helperPdfService.createSubHeader({
          title: 'AVAILABLE FUNDS'
        })
      );
      content.push({
        table: {
          widths: ['25%', '25%', '25%', '25%'],
          body: [
            [
              {
                text: [
                  {
                    text: `${
                      totalDifference >= 0 ? `Funds surplus (B-A)` : `Funds deficit (B-A)`
                    }\n`,
                    style: 'label'
                  },
                  {
                    text: totalDifference?.formatWithCurrency(currentCurrency, 0),
                    style: 'value'
                  }
                ]
              },
              '',
              '',
              ''
            ]
          ]
        },
        layout: 'noBorders'
      });

      const fLen = fundsRequiredData?.length || 0;
      for (let f = 0; f < fLen; f += 1) {
        const fundRequiredData = fundsRequiredData[f];
        const len = fundRequiredData?.length || 0;
        if (len) {
          content.push(
            helperPdfService.createSubHeader({
              title: `SECURITY #${_.add(f, 1)}`
            })
          );
          content.push({
            table: {
              widths: ['25%', '25%', '25%', '25%'],
              body: []
            },
            layout: 'noBorders'
          });
          for (let i = 0; i < fundRequiredData?.length; i += 1) {
            content[content.length - 1].table.body.push(fundRequiredData[i]);
          }
        }
      }
    }
    return content;
  }
});
